"use client";

import { c as _c } from "react-compiler-runtime";
import { useState } from "react";
import { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { assessmentSessionStorageService } from "@/features/assessments/AssessmentSessionStorageService";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { useCurrentAssessment } from "@/features/assessments/AssessmentSessionProvider";
import { hasAssessmentConsentInCache } from "@/features/assessments/consent/consentUtils";
import { FaceLandmarkerProvider } from "@/features/facialRecognition";
import { StudioPhotoTipsOverlay } from "./components/StudioPhotoTipsOverlay";
import { useDemoMode } from "../../demo/useDemoMode";
import { ConfirmStage } from "./stages/ConfirmStage";
import { ConsentStage } from "./stages/ConsentStage";
import { FrontCaptureStage } from "./stages/FrontCaptureStage";
import { PhotoUploadFlow } from "./stages/PhotoUploadFlow";
import { ProfileCaptureStage } from "./stages/ProfileCaptureStage";
import type { FrontCaptureResult, ImageCaptureStage, ProfileCaptureResult } from "./types";
export function ImageCaptureStep() {
  const $ = _c(19);
  const {
    goToNextStep,
    goToStart,
    subdomainSlug,
    flowType
  } = useAssessmentStep();
  const {
    assessment,
    headers,
    isLoadingAssessment
  } = useCurrentAssessment();
  let t0;
  if ($[0] !== flowType || $[1] !== subdomainSlug) {
    t0 = {
      subdomainSlug,
      flowType
    };
    $[0] = flowType;
    $[1] = subdomainSlug;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const isDemoMode = useDemoMode(t0);
  if (isLoadingAssessment || !assessment) {
    return null;
  }
  let t1;
  if ($[3] !== assessment.frontFaceImageUrl || $[4] !== assessment.id || $[5] !== assessment.uploadedFrontFaceImage || $[6] !== flowType) {
    t1 = () => Boolean(assessment.uploadedFrontFaceImage) || Boolean(assessment.frontFaceImageUrl) || hasAssessmentConsentInCache(assessment.id, flowType);
    $[3] = assessment.frontFaceImageUrl;
    $[4] = assessment.id;
    $[5] = assessment.uploadedFrontFaceImage;
    $[6] = flowType;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const hasCapturedConsent = t1;
  if (isDemoMode) {
    let t2;
    if ($[8] !== assessment || $[9] !== goToNextStep || $[10] !== goToStart || $[11] !== headers) {
      t2 = <FaceLandmarkerProvider><PhotoUploadFlow assessment={assessment} goToNextStep={goToNextStep} goToStart={goToStart} headers={headers} /></FaceLandmarkerProvider>;
      $[8] = assessment;
      $[9] = goToNextStep;
      $[10] = goToStart;
      $[11] = headers;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    return t2;
  }
  let t2;
  if ($[13] !== assessment || $[14] !== goToNextStep || $[15] !== goToStart || $[16] !== hasCapturedConsent || $[17] !== headers) {
    t2 = <ImageCaptureFlowInner assessment={assessment} goToNextStep={goToNextStep} goToStart={goToStart} hasCapturedConsent={hasCapturedConsent} headers={headers} />;
    $[13] = assessment;
    $[14] = goToNextStep;
    $[15] = goToStart;
    $[16] = hasCapturedConsent;
    $[17] = headers;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  return t2;
}
type ImageCaptureFlowProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  hasCapturedConsent: () => boolean;
  goToNextStep: () => void;
  goToStart: () => void;
};
function ImageCaptureFlowInner(t0) {
  const $ = _c(15);
  const {
    assessment,
    headers,
    hasCapturedConsent,
    goToNextStep,
    goToStart
  } = t0;
  let t1;
  if ($[0] !== assessment.id) {
    t1 = assessmentSessionStorageService.getPersistedStudioCapture(assessment.id);
    $[0] = assessment.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const persistedStudioCapture = t1;
  let t2;
  if ($[2] !== assessment || $[3] !== persistedStudioCapture) {
    t2 = () => getInitialImageCaptureStage({
      assessment,
      persistedStudioCapture
    });
    $[2] = assessment;
    $[3] = persistedStudioCapture;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [current, setCurrent] = useState(t2);
  const requiresFaceLandmarker = current.stage !== "consent" && current.stage !== "tips";
  let t3;
  if ($[5] !== assessment || $[6] !== current || $[7] !== goToNextStep || $[8] !== goToStart || $[9] !== hasCapturedConsent || $[10] !== headers) {
    t3 = renderImageCaptureStage(current, setCurrent, {
      assessment,
      headers,
      hasCapturedConsent,
      goToNextStep,
      goToStart
    });
    $[5] = assessment;
    $[6] = current;
    $[7] = goToNextStep;
    $[8] = goToStart;
    $[9] = hasCapturedConsent;
    $[10] = headers;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== requiresFaceLandmarker || $[13] !== t3) {
    t4 = <FaceLandmarkerProvider enabled={requiresFaceLandmarker}>{t3}</FaceLandmarkerProvider>;
    $[12] = requiresFaceLandmarker;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
type ImageCaptureStageActions = Pick<ImageCaptureFlowProps, "assessment" | "headers" | "hasCapturedConsent" | "goToNextStep" | "goToStart">;
function renderImageCaptureStage(current: ImageCaptureStage, setCurrent: (stage: ImageCaptureStage) => void, {
  assessment,
  headers,
  hasCapturedConsent,
  goToNextStep,
  goToStart
}: ImageCaptureStageActions): React.ReactElement {
  switch (current.stage) {
    // Reached from the tips sheet, never before it: the visitor sees what we are about to capture
    // ("Front + one profile angle") before being asked to consent to us capturing it.
    case "consent":
      return <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black/90">
          <ConsentStage assessment={assessment} headers={headers} onAccepted={() => setCurrent({
          stage: "frontCapture"
        })} onDeclined={goToStart} />
        </div>;
    case "tips":
      return <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black/90">
          <StudioPhotoTipsOverlay open onStart={() => setCurrent(hasCapturedConsent() ? {
          stage: "frontCapture"
        } : {
          stage: "consent"
        })}
        // No consent hop here: PhotoUploadFlow gates itself on the same cached consent, so the
        // upload path already asks before the file picker (and skips it once consent exists).
        onUploadInstead={() => setCurrent({
          stage: "frontUpload"
        })} />
        </div>;
    case "frontUpload":
      return <PhotoUploadFlow assessment={assessment} goToNextStep={goToNextStep} goToStart={goToStart} headers={headers} onBackToTips={() => setCurrent({
        stage: "tips"
      })} />;
    case "frontCapture":
      return <FrontCaptureStage assessment={assessment} headers={headers} platformId={assessment.platformId} skipIntro={current.retake} onSwitchToUpload={() => setCurrent({
        stage: "frontUpload"
      })} onComplete={(result: FrontCaptureResult) => {
        if (current.profileResult) {
          setCurrent({
            stage: "confirm",
            frontResult: result,
            profileResult: current.profileResult
          });
          return;
        }
        setCurrent({
          stage: "profileCapture",
          frontResult: result
        });
      }} />;
    case "profileCapture":
      return <ProfileCaptureStage assessment={assessment} headers={headers} platformId={assessment.platformId} skipIntro={current.retake} onSwitchToUpload={() => setCurrent({
        stage: "frontUpload"
      })} onComplete={(result: ProfileCaptureResult) => {
        setCurrent({
          stage: "confirm",
          frontResult: current.frontResult,
          profileResult: result
        });
      }} />;
    case "confirm":
      return <ConfirmStage assessment={assessment} frontResult={current.frontResult} headers={headers} profileResult={current.profileResult} onConfirmed={goToNextStep} onRetakeAll={() => setCurrent({
        stage: "frontCapture",
        retake: true
      })} onRetakeFront={() => setCurrent({
        stage: "frontCapture",
        retake: true,
        profileResult: current.profileResult
      })} onRetakeProfile={() => setCurrent({
        stage: "profileCapture",
        frontResult: current.frontResult,
        retake: true
      })} />;
  }
}
type GetInitialImageCaptureStageArgs = {
  assessment: DisplayAgelessAssessment;
  persistedStudioCapture: ReturnType<typeof assessmentSessionStorageService.getPersistedStudioCapture>;
};
function getInitialImageCaptureStage({
  assessment,
  persistedStudioCapture
}: GetInitialImageCaptureStageArgs): ImageCaptureStage {
  const frontImageId = persistedStudioCapture?.frontResult?.uploadedImageId ?? assessment.uploadedFrontFaceImage;
  switch (persistedStudioCapture?.requestedRetake) {
    case "front":
      return {
        stage: "frontCapture",
        profileResult: persistedStudioCapture.profileResult,
        retake: true
      };
    case "profile":
      if (frontImageId) {
        return {
          stage: "profileCapture",
          frontResult: {
            uploadedImageId: frontImageId
          },
          retake: true
        };
      }
      return {
        stage: "frontCapture",
        retake: true
      };
    case "both":
      return {
        stage: "frontCapture",
        retake: true
      };
    default:
      return {
        stage: "tips"
      };
  }
}